/*
  Preferencias de etiqueta por producto/variación.

  Persiste: nombre/variación acortados + layout absoluto (posición y tamaño de
  cada elemento en mm/pt). El layout reemplaza los viejos campos qr/priceSize/etc.

  Fuente compartida: el backend (MongoDB) — así lo editado en un dispositivo (p. ej.
  la web) se ve reflejado en otro (p. ej. el iPhone). localStorage funciona como
  caché local instantánea para lectura síncrona; se hidrata desde el servidor al
  abrir la app / la pestaña de etiquetas, y cada guardado se empuja al servidor.

  Clave: "var:{variationId}" o "prod:{productId}"
*/
import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LS_KEY = 'label_short_names';

// Defaults de fuente para inicializar el layout cuando no hay datos guardados.
export const FONT_DEFAULTS = {
  priceSize: 9,
  nameSize:  4,
  varSize:   3.5,
};

export function labelKey(productId, variationId) {
  return variationId ? `var:${variationId}` : `prod:${productId}`;
}

function loadAll() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {}; }
  catch { return {}; }
}

function persistAll(map) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch {}
}

// ── Suscripción para re-render tras la sincronización remota ──────────────────
const listeners = new Set();
function notify() { listeners.forEach((cb) => { try { cb(); } catch {} }); }

export function getShortNames(productId, variationId) {
  const entry = loadAll()[labelKey(productId, variationId)];
  return entry && typeof entry === 'object' ? entry : {};
}

export function loadAllShortNames() {
  return loadAll();
}

// Guarda nombre/variación cortos y el layout visual completo.
// Escribe localStorage de inmediato (optimista) y empuja al servidor.
// Si nada difiere de los originales/defaults, limpia la entrada (local y remota).
export function saveShortNames(productId, variationId, {
  name, variation, layout,
  originalName, originalVariation,
}) {
  const map = loadAll();
  const key = labelKey(productId, variationId);
  const entry = {};

  const n = (name || '').trim();
  const v = (variation || '').trim();
  if (n && n !== (originalName || '').trim()) entry.name = n;
  if (v && v !== (originalVariation || '').trim()) entry.variation = v;
  if (layout && typeof layout === 'object' && Object.keys(layout).length > 0) {
    entry.layout = layout;
  }

  if (Object.keys(entry).length === 0) {
    if (map[key]) { delete map[key]; persistAll(map); }
    pushToServer(productId, variationId, {});
    return;
  }
  map[key] = entry;
  persistAll(map);
  pushToServer(productId, variationId, entry);
}

// Empuje best-effort al backend. Si falla (offline), el valor local ya quedó
// guardado; la próxima hidratación exitosa reconciliará contra el servidor.
function pushToServer(productId, variationId, entry) {
  try {
    fetch(`${API_URL}/api/label-preferences`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        productId,
        variationId: variationId || null,
        name: entry.name || '',
        variation: entry.variation || '',
        layout: entry.layout || null,
      }),
    }).catch(() => {});
  } catch {}
}

// Trae TODAS las preferencias del servidor y las mezcla con la caché local:
//  - El servidor gana en las claves que tiene (así se propagan las ediciones
//    hechas en otros dispositivos, p. ej. de la web al iPhone).
//  - Se preservan las claves que solo existen localmente (datos previos a esta
//    sincronización que aún viven solo en localStorage). No se pierden; se suben
//    al servidor la próxima vez que se re-guarden (ahí sí tenemos el productId).
// Notifica a los suscriptores para que re-rendericen con los datos frescos.
let hydratePromise = null;
export function hydrateFromServer() {
  if (hydratePromise) return hydratePromise;
  hydratePromise = fetch(`${API_URL}/api/label-preferences`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      if (!data || !data.preferences || typeof data.preferences !== 'object') return;
      const merged = { ...loadAll(), ...data.preferences };
      persistAll(merged);
      notify();
    })
    .catch(() => {})
    .finally(() => { hydratePromise = null; });
  return hydratePromise;
}

// Hook: hidrata desde el servidor al montar y fuerza re-render cuando llegan datos
// nuevos (o tras un guardado). Devuelve un contador de versión para usar en deps.
export function useLabelPrefsSync() {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((x) => x + 1);
    listeners.add(bump);
    hydrateFromServer();
    return () => { listeners.delete(bump); };
  }, []);
  return version;
}

// Devuelve los valores a mostrar: versión guardada (o los originales/defaults).
// Sigue leyendo los campos viejos (qr, priceSize, etc.) para backward compat
// con datos guardados antes de la migración al layout absoluto.
export function resolveDisplayNames(productId, variationId, originalName, originalVariation) {
  const saved = getShortNames(productId, variationId);
  return {
    name:      saved.name      || originalName      || '',
    variation: saved.variation || originalVariation || '',
    layout:    saved.layout    || null,
    // Seeds backward-compat para computeLayout (ignorados cuando layout != null)
    qr:        (typeof saved.qr        === 'number') ? saved.qr        : 1,
    priceSize: (typeof saved.priceSize === 'number') ? saved.priceSize : FONT_DEFAULTS.priceSize,
    nameSize:  (typeof saved.nameSize  === 'number') ? saved.nameSize  : FONT_DEFAULTS.nameSize,
    varSize:   (typeof saved.varSize   === 'number') ? saved.varSize   : FONT_DEFAULTS.varSize,
  };
}
